import React, { useContext, useEffect, useRef, useState } from "react";

import { COLOR_VALUES } from "shared/config/colors";
import HStack from "shared/components/layout/stack/HStack";
import { ServerSidePaginationContext } from "shared/contexts/ServerSidePaginationContext";
import Table from "shared/components/data-display/tables/Table";
import TableFilters from "shared/components/data-display/tables/TableFilters";
import VStack from "shared/components/layout/stack/VStack";
import { getCellWidthInPercent } from "features/admin/clients/ClientsTable";
import useAbsenseReasons from "features/admin/absense-reasons/useAbsenseReasons";

const columns = [
    {
        headCellProps: {
            text: "Id",
            width: getCellWidthInPercent(50),
        },
        getBodyCellProps: (cell) => ({
            text: cell.value,
        }),
        accessor: "reasonId",
        show: true,
    },
    {
        headCellProps: {
            text: "DO",
            width: getCellWidthInPercent(50),
        },

        getBodyCellProps: (cell) => ({
            text: cell.value,
        }),
        accessor: "displayOrder",
        show: true,
    },
    {
        headCellProps: {
            text: "Name",
            width: getCellWidthInPercent(120),
        },

        getBodyCellProps: (cell) => ({
            text: cell.value,
        }),
        accessor: "baseName",
        show: true,
    },
    {
        headCellProps: {
            text: "Slot",
            width: getCellWidthInPercent(70),
        },

        getBodyCellProps: (cell) => ({
            text: cell.value,
        }),
        accessor: "allotmentTypeId",
        show: true,
    },
    {
        headCellProps: {
            text: "Code",
            width: getCellWidthInPercent(90),
        },

        getBodyCellProps: (cell) => ({
            text: cell.value,
        }),
        accessor: "code",
        show: true,
    },
    {
        headCellProps: {
            text: "Show",
            width: getCellWidthInPercent(50),
            isNextLine: true,
        },

        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "showOnTimesheet",
        show: true,
    },
    {
        headCellProps: {
            text: "Super App.",
            width: getCellWidthInPercent(60),
            isNextLine: true,
        },

        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "superApprovalRequired",
        show: true,
    },
    {
        headCellProps: {
            text: "Limit",
            width: getCellWidthInPercent(60),
            isNextLine: true,
        },

        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "limitByAllotment",
        disableSortBy: true,
        show: true,
    },
    {
        headCellProps: {
            text: `Show on \nLeave`,
            width: getCellWidthInPercent(80),
            isNextLine: true,
        },

        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "showOnLeave",
        show: true,
    },
    {
        headCellProps: {
            text: `Auto \nApproved`,
            width: getCellWidthInPercent(70),
            isNextLine: true,
        },

        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "autoApproveTimsheetWeek",
        show: true,
    },
    {
        headCellProps: {
            text: "Unpaid",
            width: getCellWidthInPercent(70),
        },

        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "isUnPaidLeave",
        show: true,
    },
    {
        headCellProps: {
            text: "Status",
            width: getCellWidthInPercent(60),
        },

        getBodyCellProps: (cell) => ({
            icon: cell.value ? "success" : "error",
        }),
        accessor: "active",
        show: true,
       
    },
    {
        headCellProps: {
            text: "Offices",
            width: getCellWidthInPercent(130),
        },

        getBodyCellProps: (cell) => ({
            text: cell.value,
            tooltip: cell.value,
            placement: "bottom-start",
        }),
        accessor: "officeShortNames",
        show: true,
        disableSortBy: true,
    },
];

const getRowProps = (row) => ({
    style: {
        background: row.values.active === false ? COLOR_VALUES.Gray_9 : "",
    },
});

const FiltersConfig = [
    {
        name: "active",
        columnAccessor: "active",
        unfilteredValue: "all",
        isSegmentedControl: true,
        options: [
            {
                value: "all",
                label: "All",
            },
            {
                value: "active",
                label: "Active",
            },
            {
                value: "inactive",
                label: "Inactive",
            },
        ],
    },
];

const AbsenseReasonsTable = () => {
    const { queryPageIndex, queryPageSize, querySortBy, totalItemsUpdate } =
        useContext(ServerSidePaginationContext);
    const { useAbsenseReasonsList } = useAbsenseReasons();
    const [columnsNew, setColumnsNew] = useState(columns);
    const [activeFilter, setActiveFilter] = useState([]);

    const showFilterVal = useRef();

    switch (
    activeFilter?.find((f) => f.id === "active")?.value
    ) {
        case undefined:
            showFilterVal.current = "";
            break;
        case "active":
            showFilterVal.current = true;
            break;
        case "inactive":
            showFilterVal.current = false;
            break;
        default:
            showFilterVal.current = true;
            break;
    }

    const absenseReasonsDataApi = useAbsenseReasonsList({
        sortBy: querySortBy,
        pageSize: queryPageSize,
        pageIndex: queryPageIndex,
        active: showFilterVal.current,
    });

    useEffect(() => {
        const i = columns.findIndex((f) => f.headCellProps.text === "Code");

        setColumnsNew((prevColumns) => {

            let newColArr = [...prevColumns];
            
            newColArr[i].getBodyCellProps = (cell) => ({
                text: cell.value ?? "-",
                actionIcon: "caret-right",
            });

            return newColArr;
        });
    }, []);

    useEffect(() => {
        if (!absenseReasonsDataApi.isLoading) {
            totalItemsUpdate(absenseReasonsDataApi?.data?.totalItems);
        }
    }, [absenseReasonsDataApi?.data?.totalItems, absenseReasonsDataApi.isLoading, totalItemsUpdate]);

    if (absenseReasonsDataApi.isLoading) {
        return <>Loading...</>;
    }

    if (absenseReasonsDataApi.isError) {
        return <>Error</>;
    }

    return (
        <VStack>
                <HStack align="center" justify="space-between">
                    <HStack align="flex-end">
                        <TableFilters
                            initialFilterData={[
                                {
                                    name: "active",
                                    value: "active"
                                },
                            ]}
                            onChange={setActiveFilter}
                            filtersConfig={FiltersConfig}
                        />
                    </HStack>
                </HStack>
                <Table
                    dataIsFetching={absenseReasonsDataApi.isFetching}
                    columns={columnsNew}
                    data={absenseReasonsDataApi?.data?.items}
                    getRowProps={getRowProps}
                    paginationFromFE={false}
                />
            </VStack>
    );
};

export default React.memo(AbsenseReasonsTable);
